<template>
  <div class="supplier-due">
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Supplier</label>
          <FormDropdown v-model="filters.supplier" :options="supplierOptions" placeholder="All suppliers" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Search</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Search supplier name or phone" />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <button type="button" class="rp-card rp-card-action tone-red" :class="{ selected: filters.show === 'due' }"
              :title="filters.show === 'due' ? 'Show all suppliers' : 'Show suppliers still owed'" @click="toggleShow('due')">
        <span class="rp-card-icon"><i class="ti ti-clock-dollar"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Payable</div>
          <div class="rp-card-value text-danger">{{ formatMoney(summary.payable) }}</div>
          <div class="rp-card-sub">
            owed to {{ plural(summary.due_count, 'supplier') }} on {{ formatDate(filters.date_to) }}
          </div>
        </div>
      </button>
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-shopping-cart"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Purchased · Returned</div>
          <div class="rp-card-value">{{ formatMoney(Number(summary.purchased) + Number(summary.other_bills)) }}</div>
          <div class="rp-card-sub">{{ formatMoney(summary.returned) }} returned in the period</div>
        </div>
      </div>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-cash"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Paid in Period</div>
          <div class="rp-card-value">{{ formatMoney(summary.paid) }}</div>
          <div class="rp-card-sub">opening balance {{ formatMoney(summary.opening) }}</div>
        </div>
      </div>
      <button type="button" class="rp-card rp-card-action tone-teal" :class="{ selected: filters.show === 'advance' }"
              :title="filters.show === 'advance' ? 'Show all suppliers' : 'Show suppliers paid in advance'"
              @click="toggleShow('advance')">
        <span class="rp-card-icon"><i class="ti ti-coin"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Advance · Over 90 days</div>
          <div class="rp-card-value">{{ formatMoney(summary.advance) }}</div>
          <div class="rp-card-sub">
            {{ plural(summary.advance_count, 'supplier') }} in advance ·
            <span :class="{ 'text-danger': Number(summary.overdue_90) }">{{ formatMoney(summary.overdue_90) }} over 90 days</span>
          </div>
        </div>
      </button>
    </div>

    <div class="rp-insights" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title">Payable movement</div>
        <div class="rp-bars">
          <div v-for="step in flowBars" :key="step.label">
            <div class="d-flex justify-content-between small mb-1">
              <span>{{ step.label }}</span>
              <span class="fw-semibold text-nowrap" :class="step.text">{{ step.sign }}{{ formatMoney(step.value) }}</span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :class="step.tone" :style="{ width: `${step.percent}%` }"></div></div>
          </div>
        </div>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Largest dues</div>
        <ul v-if="summary.top_dues.length" class="rp-ranked">
          <li v-for="(supplier, index) in summary.top_dues" :key="supplier.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="supplier.name">{{ supplier.name }}</div>
              <small class="text-muted">
                <template v-if="supplier.oldest_due_days !== null">oldest bill {{ plural(supplier.oldest_due_days, 'day') }}</template>
                <template v-else>{{ supplier.phone || '—' }}</template>
              </small>
            </div>
            <span class="fw-semibold text-nowrap text-danger">{{ formatMoney(supplier.closing) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">Nothing is owed to suppliers.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Highlights</div>
        <ul class="rp-highlights">
          <li v-for="item in highlights" :key="item.label" :class="item.tone">
            <span class="hl-icon"><i class="ti" :class="item.icon"></i></span>
            <div class="min-w-0">
              <small class="text-muted d-block">{{ item.label }}</small>
              <div class="fw-semibold text-truncate" :title="item.name">{{ item.name }}</div>
              <small class="text-muted">{{ item.detail }}</small>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button v-for="pill in showPills" :key="pill.key" type="button" class="rp-pill"
                  :class="{ active: (filters.show || 'all') === pill.key }" @click="setShow(pill.key)">
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}<i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button v-if="row.bills.length" type="button" class="rp-expand"
                          :title="expanded[row.key] ? 'Hide unpaid bills' : 'Show unpaid bills'" @click="toggleRow(row.key)">
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td>
                  <div class="fw-medium">{{ row.supplier }}</div>
                  <span v-if="row.supplier_phone" class="rp-sub">{{ row.supplier_phone }}</span>
                </td>
                <td class="text-end" :class="moneyClass(row.opening)">{{ balanceText(row.opening) }}</td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.purchased) && !Number(row.other_bills) }">
                  {{ formatMoney(Number(row.purchased) + Number(row.other_bills)) }}
                  <span v-if="Number(row.other_bills)" class="rp-sub">incl. {{ formatMoney(row.other_bills) }} other bills</span>
                </td>
                <td class="text-end" :class="Number(row.returned) ? 'text-info' : 'text-muted'">
                  {{ Number(row.returned) ? `−${formatMoney(row.returned)}` : '—' }}
                </td>
                <td class="text-end" :class="Number(row.paid) ? 'text-success' : 'text-muted'">
                  {{ Number(row.paid) ? `−${formatMoney(row.paid)}` : '—' }}
                </td>
                <td class="text-end fw-semibold" :class="moneyClass(row.closing)">
                  {{ balanceText(row.closing) }}
                  <span v-if="Number(row.closing) < -0.005" class="rp-sub">advance</span>
                </td>
                <td class="text-center">
                  <span class="rp-count" :class="{ zero: !row.unpaid_count }">{{ row.unpaid_count }}</span>
                  <span v-if="row.oldest_due_days !== null" class="rp-sub" :class="{ 'text-danger': row.oldest_due_days > 90 }">
                    oldest {{ plural(row.oldest_due_days, 'day') }}
                  </span>
                </td>
                <td class="text-nowrap">
                  {{ formatDate(row.last_payment) }}
                  <span v-if="row.last_purchase" class="rp-sub">bought {{ formatDate(row.last_purchase) }}</span>
                </td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="rp-detail-title">
                      <i class="ti ti-file-invoice me-1"></i>Unpaid bills of {{ row.supplier }}
                      <span class="text-muted fw-normal">· payments clear the oldest bills first</span>
                    </div>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Bill</th>
                            <th>Date</th>
                            <th>Type</th>
                            <th class="text-end">Amount</th>
                            <th class="text-end">Returned</th>
                            <th class="text-end">Paid</th>
                            <th class="text-end">Due</th>
                            <th class="text-end">Age</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="bill in row.bills" :key="bill.voucher_id">
                            <td class="fw-semibold">{{ bill.voucher_id }}</td>
                            <td class="text-nowrap">{{ formatDate(bill.date) }}</td>
                            <td>{{ bill.kind === 'purchase' ? 'Purchase' : 'Other bill' }}</td>
                            <td class="text-end">{{ formatMoney(bill.amount) }}</td>
                            <td class="text-end">{{ Number(bill.returned) ? `−${formatMoney(bill.returned)}` : '—' }}</td>
                            <td class="text-end">{{ Number(bill.paid) ? formatMoney(bill.paid) : '—' }}</td>
                            <td class="text-end fw-semibold text-danger">{{ formatMoney(bill.due) }}</td>
                            <td class="text-end">
                              <span class="rp-badge" :class="ageTone(bill.age_days)">{{ plural(bill.age_days, 'day') }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-users"></i>
                  <div class="fw-semibold">No suppliers found</div>
                  <small class="text-muted">Try another period, supplier or filter.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th></th>
              <th class="text-end">Total ({{ plural(rows.length, 'supplier') }})</th>
              <th class="text-end">{{ formatMoney(summary.opening) }}</th>
              <th class="text-end">{{ formatMoney(Number(summary.purchased) + Number(summary.other_bills)) }}</th>
              <th class="text-end">{{ Number(summary.returned) ? `−${formatMoney(summary.returned)}` : '—' }}</th>
              <th class="text-end">{{ Number(summary.paid) ? `−${formatMoney(summary.paid)}` : '—' }}</th>
              <th class="text-end">{{ formatMoney(summary.closing) }}</th>
              <th class="text-center">{{ formatInt(summary.unpaid_count) }}</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination v-if="visibleRows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                        :start="pageStart" :end="pageEnd" :total="visibleRows.length" @go="goToPage" />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Balances of the store picked in the header (its bills, returns and payments). Closing = opening + purchased − returned − paid; a negative closing is an advance.
      Payments clear each supplier's oldest bills first, which gives the unpaid bills and their age.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport from '../sales/salesReportMixin';
import purchaseReport from './purchaseReportMixin';

const EMPTY_SUMMARY = {
  opening: 0, purchased: 0, other_bills: 0, returned: 0, paid: 0, closing: 0, payable: 0, advance: 0,
  supplier_count: 0, due_count: 0, advance_count: 0, overdue_90: 0, unpaid_count: 0, top_dues: [],
  show_counts: { all: 0, due: 0, advance: 0, active: 0 },
};

export default {
  name: 'SupplierDueReport',
  mixins: [inventoryReport, periodReport, salesReport, purchaseReport],

  reportUrl: apiUrls.supplier_due_report.get,
  fileName: 'supplier_due',
  columns: [
    { key: 'supplier', label: 'Supplier' },
    { key: 'opening', label: 'Opening', class: 'text-end', numeric: true },
    { key: 'purchased', label: 'Purchased', class: 'text-end', numeric: true },
    { key: 'returned', label: 'Returned', class: 'text-end', numeric: true },
    { key: 'paid', label: 'Paid', class: 'text-end', numeric: true },
    { key: 'closing', label: 'Closing', class: 'text-end', numeric: true },
    { key: 'unpaid_count', label: 'Unpaid Bills', class: 'text-center', numeric: true },
    { key: 'last_payment', label: 'Last Payment' },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), supplier: null, show: null, warehouse: null, category: null, sub_category: null,
        search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
    };
  },

  computed: {
    hasActiveFilters() {
      const { supplier, show, search } = this.filters;
      return this.periodChanged || !!(supplier || show || search.trim());
    },

    showPills() {
      const counts = this.summary.show_counts;
      return [
        { key: 'all', label: 'All suppliers', count: counts.all },
        { key: 'due', label: 'Owed', count: counts.due },
        { key: 'advance', label: 'Advance', count: counts.advance },
        { key: 'active', label: 'Active in period', count: counts.active },
      ];
    },

    flowBars() {
      const s = this.summary;
      const steps = [
        { label: 'Opening balance', value: s.opening, sign: '', tone: 'info', text: '' },
        { label: 'Purchased', value: Number(s.purchased) + Number(s.other_bills), sign: '+', tone: 'warning', text: '' },
        { label: 'Returned', value: s.returned, sign: '−', tone: 'info', text: 'text-info' },
        { label: 'Paid', value: s.paid, sign: '−', tone: 'success', text: 'text-success' },
        { label: 'Closing balance', value: s.closing, sign: '', tone: 'danger', text: 'text-danger' },
      ];
      const max = Math.max(0, ...steps.map((st) => Math.abs(Number(st.value) || 0)));
      return steps.map((st) => {
        const v = Math.abs(Number(st.value) || 0);
        return { ...st, percent: max && v ? Math.max(4, Math.round((v / max) * 100)) : 0 };
      });
    },

    highlights() {
      const owing = this.rows.filter((r) => r.oldest_due_days !== null);
      const oldest = owing.reduce((best, r) => (!best || r.oldest_due_days > best.oldest_due_days ? r : best), null);
      const advance = this.rows.reduce((best, r) => (Number(r.closing) < (best ? Number(best.closing) : -0.005) ? r : best), null);
      const idle = owing.filter((r) => !r.last_payment || r.last_payment < this.filters.date_from).length;
      return [
        {
          label: 'Oldest unpaid bill', tone: 'tone-red', icon: 'ti-hourglass-high',
          name: oldest ? oldest.supplier : '—',
          detail: oldest ? `${this.plural(oldest.oldest_due_days, 'day')} · since ${this.formatDate(oldest.oldest_due_date)}` : 'No unpaid bills',
        },
        {
          label: 'Over 90 days', tone: 'tone-orange', icon: 'ti-alert-triangle',
          name: this.formatMoney(this.summary.overdue_90),
          detail: `on ${this.plural(this.summary.unpaid_count, 'unpaid bill')} in total`,
        },
        {
          label: 'Largest advance', tone: 'tone-teal', icon: 'ti-coin',
          name: advance ? advance.supplier : '—',
          detail: advance ? `${this.formatMoney(-Number(advance.closing))} paid ahead` : 'No supplier is paid in advance',
        },
        {
          label: 'Owed but not paid this period', tone: 'tone-navy', icon: 'ti-calendar-off',
          name: this.plural(idle, 'supplier'),
          detail: 'no payment since the period began',
        },
      ];
    },

    visibleRows() {
      return this.sortKey ? this.sortRows(this.rows) : this.rows;
    },
  },

  watch: {
    'filters.show'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      const { show } = this.filters;
      return { ...this.periodParams(), ...this.supplierParams(), ...(show ? { show } : {}) };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { ...defaultPeriod(this.today), supplier: null, show: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
    },

    setShow(key) {
      this.filters.show = key === 'all' ? null : key;
    },

    toggleShow(key) {
      this.filters.show = this.filters.show === key ? null : key;
    },

    moneyClass(value) {
      const n = Number(value) || 0;
      return n > 0.005 ? 'text-danger' : n < -0.005 ? 'text-success' : 'text-muted';
    },

    balanceText(value) {
      const n = Number(value) || 0;
      return Math.abs(n) < 0.005 ? '—' : this.formatMoney(Math.abs(n));
    },

    ageTone(days) {
      if (days > 90) return 'danger';
      if (days > 30) return 'warning';
      return 'muted';
    },

    printReport() {
      const s = this.summary;
      const show = this.showPills.find((p) => p.key === (this.filters.show || 'all'))?.label;
      const search = this.filters.search.trim();
      this.printColumns(
        'Supplier Due Report',
        [this.periodText(), `Showing: ${show}`, this.supplierText(), this.storeText(), search && `Search: ${search}`],
        [
          ['Payable', `${this.formatMoney(s.payable)} · ${this.plural(s.due_count, 'supplier')}`],
          ['Purchased · Returned', `${this.formatMoney(Number(s.purchased) + Number(s.other_bills))} · ${this.formatMoney(s.returned)}`],
          ['Paid in Period', this.formatMoney(s.paid)],
          ['Advance · Over 90 days', `${this.formatMoney(s.advance)} · ${this.formatMoney(s.overdue_90)}`],
        ],
        [
          { label: 'Supplier', value: (r) => r.supplier },
          { label: 'Phone', value: (r) => r.supplier_phone },
          { label: 'Opening', value: (r) => this.formatMoney(r.opening), num: true },
          { label: 'Purchased', value: (r) => this.formatMoney(Number(r.purchased) + Number(r.other_bills)), num: true },
          { label: 'Returned', value: (r) => this.formatMoney(r.returned), num: true },
          { label: 'Paid', value: (r) => this.formatMoney(r.paid), num: true },
          { label: 'Closing', value: (r) => this.formatMoney(r.closing), num: true },
          { label: 'Unpaid Bills', value: (r) => r.unpaid_count, num: true },
          { label: 'Oldest (days)', value: (r) => r.oldest_due_days ?? '', num: true },
          { label: 'Last Payment', value: (r) => (r.last_payment ? this.formatDate(r.last_payment) : '') },
        ],
        this.visibleRows,
        {
          Phone: 'Total', Opening: this.formatMoney(s.opening),
          Purchased: this.formatMoney(Number(s.purchased) + Number(s.other_bills)), Returned: this.formatMoney(s.returned),
          Paid: this.formatMoney(s.paid), Closing: this.formatMoney(s.closing), 'Unpaid Bills': s.unpaid_count,
        },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>
